import { css } from 'styled-components'

// 모바일 브레이크포인트
const MOBILE_BREAKPOINT = 767
const mobile = `@media (max-width: ${MOBILE_BREAKPOINT}px)`

// 뷰포트 너비 기준 계산 함수 (375px 기준)
const vw = (px: number, base = 375, unit = 'vw') => `${(px / base) * 100}${unit}`

// 색상 CSS 변수
const colorVariables = css`
  --primary-yellow100: #f1b13c;
  --primary-yellow50: #82642f;
  --primary-yellow20: #3f3627;
  --primary-yellow15: #342e26;
  --primary-yellow10: #292625;
  --primary-navy900: #0f131c;
  --primary-navy800: #131722;
  --primary-navy700: #1b1f2c;
  --primary-navy600: #262936;
  --primary-navy500: #30333f;
  --primary-navy400: #64666e;
  --grayscale-gray900: #212121;
  --grayscale-gray800: #505050;
  --grayscale-gray600: #757575;
  --grayscale-gray500: #9e9e9e;
  --grayscale-gray400: #bdbdbd;
  --grayscale-gray50: #fafafa;
  --secondary-brightRed100: #ff453a;
  --secondary-brightRed50: #892e2e;
  --secondary-brightRed15: #361e26;
  --secondary-darkRed100: #c1464e;
  --secondary-darkRed50: #6a2e38;
  --secondary-darkRed15: #2d1e29;
  --secondary-emerald100: #45c9ab;
  --secondary-emerald50: #2c7066;
  --secondary-emerald15: #1b3237;
  --secondary-green100: #31e038;
  --secondary-green50: #227c2d;
  --secondary-green15: #183525;
  --secondary-blue100: #0383fa;
  --secondary-blue50: #0b4d8e;
  --secondary-blue15: #112742;
  --secondary-black100: #000000;
  --secondary-black80: #000000cc;
  --secondary-white100: #ffffff;
  --secondary-white80: #ffffffcc;
  --chart-1: #079992;
  --chart-2: #0a3d62;
  --chart-3: #0c2461;
  --chart-4: #b71540;
  --chart-5: #e58e26;
  --chart-6: #38ada9;
  --chart-7: #3c6382;
  --chart-8: #187abd;
  --chart-9: #e55039;
  --chart-10: #f6b93b;
  --chart-11: #78e08f;
  --chart-12: #60a3bc;
  --chart-13: #fad390;
  --chart-14: #6a89cc;
  --chart-15: #b8e994;
  --chart-16: #a0a0a0;
  --gradient-greenBlue: linear-gradient(#2db48b, #1b67bf);
  --gradient-purpleBlue: linear-gradient(#6c2ab1, #399ede);
  --gradient-pinkRed: linear-gradient(#a540f9, #ba1641);
  --gradient-OrangeYellow: linear-gradient(#ca4938, #e3b456);
  --gradient-yellowOrange: linear-gradient(#e3b456, #ca4938);
  --gradient-brightDarkNavy: linear-gradient(#1f273a, #1b1f2c);
  --gradient-tangerineYellow: linear-gradient(#fb992a, #fdc954);
  --gradientColor-green: #2db48b;
  --gradientColor-darkBlue: #1b67bf;
  --gradientColor-purple: #6c2ab1;
  --gradientColor-blue: #399ede;
  --gradientColor-pink: #a540f9;
  --gradientColor-red: #ba1641;
  --gradientColor-darkYellow: #e3b456;
  --gradientColor-orange: #ca4938;
  --gradientColor-brightDark: #1f273a;
  --gradientColor-navy: #1b1f2c;
  --gradientColor-tangerine: #fb992a;
  --gradientColor-lightYellow: #fdc954;
`

// 스페이싱 CSS 변수
const spacingVariables = css`
  --spacing-100: 0.4rem;
  --spacing-200: 0.8rem;
  --spacing-300: 1.2rem;
  --spacing-400: 1.6rem;
  --spacing-500: 2rem;
  --spacing-600: 2.4rem;
  --spacing-700: 2.8rem;
  --spacing-800: 3.2rem;
  --spacing-900: 3.6rem;
  --spacing-1000: 4rem;
  --spacing-1100: 4.4rem;
  --spacing-1200: 4.8rem;
  --spacing-1300: 5.2rem;
  --spacing-1400: 5.6rem;
  --spacing-1500: 6rem;

  ${mobile} {
    --spacing-100: ${vw(4)};
    --spacing-200: ${vw(8)};
    --spacing-300: ${vw(12)};
    --spacing-400: ${vw(16)};
    --spacing-500: ${vw(20)};
    --spacing-600: ${vw(24)};
    --spacing-700: ${vw(28)};
    --spacing-800: ${vw(32)};
    --spacing-900: ${vw(36)};
    --spacing-1000: ${vw(40)};
    --spacing-1100: ${vw(44)};
    --spacing-1200: ${vw(48)};
    --spacing-1300: ${vw(52)};
    --spacing-1400: ${vw(56)};
    --spacing-1500: ${vw(60)};
  }
`

// 폰트 크기 CSS 변수
const fontSizeVariables = css`
  --fontsize-body5: 1rem;
  --fontsize-body4: 1.2rem;
  --fontsize-body3: 1.4rem;
  --fontsize-body2: 1.5rem;
  --fontsize-body1: 1.6rem;
  --fontsize-h7: 1.7rem;
  --fontsize-h6: 1.8rem;
  --fontsize-h5: 2rem;
  --fontsize-h4: 2.2rem;
  --fontsize-h3: 2.4rem;
  --fontsize-h2: 2.8rem;
  --fontsize-h1: 4rem;

  ${mobile} {
    --fontsize-body5: ${vw(10)};
    --fontsize-body4: ${vw(12)};
    --fontsize-body3: ${vw(14)};
    --fontsize-body2: ${vw(15)};
    --fontsize-body1: ${vw(16)};
    --fontsize-h7: ${vw(17)};
    --fontsize-h6: ${vw(18)};
    --fontsize-h5: ${vw(20)};
    --fontsize-h4: ${vw(22)};
    --fontsize-h3: ${vw(24)};
    --fontsize-h2: ${vw(28)};
    --fontsize-h1: ${vw(40)};
  }
`

// 보더 반지름 CSS 변수
const borderRadiusVariables = css`
  --border-radius-6: 0.6rem;
  --border-radius-12: 1.2rem;
  --border-radius-25: 2.5rem;

  ${mobile} {
    --border-radius-6: ${vw(6)};
    --border-radius-12: ${vw(12)};
    --border-radius-25: ${vw(25)};
  }
`

// CSS 리셋
const cssReset = css`
  /* http://meyerweb.com/eric/tools/css/reset/
     v5.0.1 | 20191019
     License: none (public domain)
  */
  html,
  body,
  div,
  span,
  applet,
  object,
  iframe,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  p,
  blockquote,
  pre,
  a,
  abbr,
  acronym,
  address,
  big,
  cite,
  code,
  del,
  dfn,
  em,
  img,
  ins,
  kbd,
  q,
  s,
  samp,
  small,
  strike,
  strong,
  sub,
  sup,
  tt,
  var,
  b,
  u,
  i,
  center,
  dl,
  dt,
  dd,
  menu,
  ol,
  ul,
  li,
  fieldset,
  form,
  label,
  legend,
  table,
  caption,
  tbody,
  tfoot,
  thead,
  tr,
  th,
  td,
  article,
  aside,
  canvas,
  details,
  embed,
  figure,
  figcaption,
  footer,
  header,
  hgroup,
  main,
  menu,
  nav,
  output,
  ruby,
  section,
  summary,
  time,
  mark,
  audio,
  video {
    margin: 0;
    padding: 0;
    border: 0;
    font-size: 100%;
    font: inherit;
    vertical-align: baseline;
  }

  /* HTML5 display-role reset for older browsers */
  article,
  aside,
  details,
  figcaption,
  figure,
  footer,
  header,
  hgroup,
  main,
  menu,
  nav,
  section {
    display: block;
  }

  /* HTML5 hidden-attribute fix for newer browsers */
  *[hidden] {
    display: none;
  }

  body {
    line-height: 1;
  }

  menu,
  ol,
  ul {
    list-style: none;
  }

  blockquote,
  q {
    quotes: none;
  }

  blockquote:before,
  blockquote:after,
  q:before,
  q:after {
    content: '';
    content: none;
  }

  table {
    border-collapse: collapse;
    border-spacing: 0;
  }
`

// 일반적인 스타일 리셋
const generalReset = css`
  * {
    outline: none;
  }

  a {
    text-decoration: none;
  }

  h2 {
    margin-block-start: 0;
    margin-block-end: 0;
  }

  input {
    border: 0;
    padding: 0;
    margin: 0;
  }

  input[type='checkbox'],
  input[type='radio'] {
    :disabled {
      + span {
        cursor: not-allowed;
      }
    }
  }

  input[type='radio'] + label,
  button {
    margin: 0;
    padding: 0;
    border: 0;
    background-color: transparent;
    cursor: pointer;
    background: none;
    padding: 0;
    :disabled {
      cursor: not-allowed;
    }
  }

  /* Change Autocomplete styles in Windows Chrome*/
  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  textarea:-webkit-autofill,
  textarea:-webkit-autofill:hover,
  textarea:-webkit-autofill:focus,
  select:-webkit-autofill,
  select:-webkit-autofill:hover,
  select:-webkit-autofill:focus {
    border: 1px solid var(--color-primary);
    -webkit-text-fill-color: var(--color-primary);
    -webkit-box-shadow: 0 0 0px 1000px #000 inset;
    transition: background-color 5000s ease-in-out 0s;
  }

  /* safari input zoom disabled */
  @media screen and (max-width: 767px) and (device-aspect-ratio: 9/16) {
    select,
    textarea,
    input[type='text'],
    input[type='password'],
    input[type='datetime'],
    input[type='datetime-local'],
    input[type='date'],
    input[type='month'],
    input[type='time'],
    input[type='week'],
    input[type='number'],
    input[type='email'],
    input[type='tel'],
    input[type='url'] {
      font-size: 16px;
    }
  }

  /* safari selectbox reset */
  select {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    /* SVG background image */
    background: blue
      url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI5IiBoZWlnaHQ9IjYiIHZpZXdCb3g9IjAgMCA5IDYiPgogICAgPGcgZmlsbD0ibm9uZSIgZmlsbC1ydWxlPSJldmVub2RkIj4KICAgICAgICA8ZyBmaWxsPSIjRkVGRUZFIj4KICAgICAgICAgICAgPGc+CiAgICAgICAgICAgICAgICA8Zz4KICAgICAgICAgICAgICAgICAgICA8cGF0aCBkPSJNNjMuNSAyMkw2OCAyOCA1OSAyOHoiIHRyYW5zZm9ybT0idHJhbnNsYXRlKC05MS4wMDAwMDAsIC0zNTguMDAwMDAwKSB0cmFuc2xhdGUoMzIuMDAwMDAwLCAzMDEuMDAwMDAwKSB0cmFuc2xhdGUoMC4wMDAwMDAsIDM1LjAwMDAwMCkgdHJhbnNsYXRlKDYzLjUwMDAwMCwgMjUuMDAwMDAwKSByb3RhdGUoLTE4MC4wMDAwMDApIHRyYW5zbGF0ZSgtNjMuNTAwMDAwLCAtMjUuMDAwMDAwKSIvPgogICAgICAgICAgICAgICAgPC9nPgogICAgICAgICAgICA8L2c+CiAgICAgICAgPC9nPgogICAgPC9nPgo8L3N2Zz4K')
      no-repeat;
    background-size: 9px;
    background-position: calc(100% - var(--spacing-100)) center;
    background-repeat: no-repeat;
    background-color: #efefef;
    display: block;
    width: 100%;
    margin: 0;
    padding-right: var(--spacing-600);
  }

  input[type='date'] {
    display: block;
    -webkit-appearance: textfield;
    -moz-appearance: textfield;
    min-height: 1.4em;
  }

  /* hide input number arrow */
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  /* Firefox */
  input[type='number'] {
    -moz-appearance: textfield;
  }
`

// 테마 CSS 변수
const themeVariables = css`
  /* theme */
  :root {
    &.light {
      ${colorVariables}
    }
    &.dark {
      ${colorVariables}
    }
    :not(.light):not(.dark) {
      @media (prefers-color-scheme: light) {
        ${colorVariables}
      }
      @media (prefers-color-scheme: dark) {
        ${colorVariables}
      }
    }
  }
`

// Spoqa Han Sans Neo 폰트 정의
const spoqaHanSansNeoFont = css`
  @font-face {
    font-family: 'Spoqa Han Sans Neo';
    font-weight: 700;
    font-display: swap;
    src: local('Spoqa Han Sans Neo Bold'),
      url('https://cdn.jsdelivr.net/gh/spoqa/spoqa-han-sans@latest/Subset/SpoqaHanSansNeo/SpoqaHanSansNeo-Bold.woff2')
        format('woff2');
  }
  @font-face {
    font-family: 'Spoqa Han Sans Neo';
    font-weight: 400;
    font-display: swap;
    src: local('Spoqa Han Sans Neo Regular'),
      url('https://cdn.jsdelivr.net/gh/spoqa/spoqa-han-sans@latest/Subset/SpoqaHanSansNeo/SpoqaHanSansNeo-Regular.woff2')
        format('woff2');
  }
`

// Apple SD Gothic Neo 폰트 정의
const appleSDGothicFont = css`
  @font-face {
    font-family: 'Apple SD Gothic Neo';
    font-weight: 700;
    font-display: swap;
    src: local('Apple SD Gothic Neo Bold'),
      url('https://www.therich.io/fonts/AppleSDGothicNeoB.woff2') format('woff2');
  }
  @font-face {
    font-family: 'Apple SD Gothic Neo';
    font-weight: 500;
    font-display: swap;
    src: local('Apple SD Gothic Neo Medium'),
      url('https://www.therich.io/fonts/AppleSDGothicNeoM.woff2') format('woff2');
  }
  @font-face {
    font-family: 'Apple SD Gothic Neo';
    font-weight: 400;
    font-display: swap;
    src: local('Apple SD Gothic Neo Regular'),
      url('https://www.therich.io/fonts/AppleSDGothicNeoR.woff2') format('woff2');
  }
`

// 디자인 시스템의 globalStyle을 대체하는 메인 스타일
export const designSystemGlobalStyle = css`
  ${cssReset}
  ${generalReset}

  :root {
    font-size: 62.5%;
    color: var(--grayscale-gray50);

    ${spacingVariables}
    ${borderRadiusVariables}
    ${fontSizeVariables}
    ${themeVariables}
  }

  body {
    /* bg color */
    background-color: var(--color-ebest-primary-white);

    * {
      font-family: 'Spoqa Han Sans Neo', 'Apple SD Gothic Neo', sans-serif;
    }
  }

  ${appleSDGothicFont}
  ${spoqaHanSansNeoFont}
`

export default designSystemGlobalStyle
